import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

interface GlobalContext {
  minimizedNotificationBox: boolean;
  setMinimizedNotificationBox: (minimized: boolean) => void;
}

// Define initial state
const initialState = {
  minimizedNotificationBox: false,
};

// Create context
const GlobalContext = createContext<GlobalContext | undefined>(undefined);

// Context provider component
export function GlobalContextProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState(initialState);

  const setMinimizedNotificationBox = useCallback((minimized: boolean) => {
    setState((state) => ({
      ...state,
      minimizedNotificationBox: minimized,
    }));
  }, []);

  const context = useMemo(
    () => ({
      setMinimizedNotificationBox: setMinimizedNotificationBox,
      minimizedNotificationBox: state.minimizedNotificationBox,
    }),
    [state, setMinimizedNotificationBox],
  );

  return (
    <GlobalContext.Provider value={context}>{children}</GlobalContext.Provider>
  );
}

export function useMinimizedNotificationBox() {
  const context = useContext(GlobalContext);
  if (!context)
    throw new Error("Global Context must be used within a StateProvider");
  return {
    minimized: context.minimizedNotificationBox,
    setMinimized: context.setMinimizedNotificationBox,
  };
}
